import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

/** One tab and the panel it reveals. */
export interface TabDefinition {
  /** Stable identifier, also the controlled value. */
  readonly id: string;
  /** The tab's label. */
  readonly label: ReactNode;
  /** The panel's contents. */
  readonly panel: ReactNode;
  /** A count or status rendered after the label. */
  readonly badge?: ReactNode;
}

export interface TabsProps {
  /** What the set of tabs is for; becomes the tablist's accessible name. */
  readonly label: string;
  /** The tabs, in order. */
  readonly tabs: readonly TabDefinition[];
  /** The selected tab id. */
  readonly value: string;
  /** Called with the newly selected tab id. */
  readonly onChange: (id: string) => void;
}

/**
 * A tab set following the ARIA authoring-practices tabs pattern.
 *
 * Manual activation, not automatic: arrow keys move focus between tabs and
 * Enter or Space selects. Automatic activation — selecting whatever the arrow
 * keys land on — is fine for cheap panels and actively hostile for this
 * product, where arrowing across "Jobs / Invoices / Equipment" would fire three
 * requests and land a screen-reader user in a panel they were only passing.
 *
 * Only the selected panel is rendered, so a panel's effects do not run until it
 * is actually shown.
 *
 * @param props see {@link TabsProps}
 * @returns the tablist and the selected panel
 */
export function Tabs({ label, tabs, value, onChange }: TabsProps): React.JSX.Element {
  const base = useId();
  // Kept as element references rather than looked up by id: an id selector
  // would have to be escaped, and tab ids come from callers.
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());

  function focusTab(index: number): void {
    const clamped = (index + tabs.length) % tabs.length;
    const tab = tabs[clamped];
    if (tab === undefined) {
      return;
    }
    tabRefs.current.get(tab.id)?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        focusTab(index + 1);
        return;
      case 'ArrowLeft':
        event.preventDefault();
        focusTab(index - 1);
        return;
      case 'Home':
        event.preventDefault();
        focusTab(0);
        return;
      case 'End':
        event.preventDefault();
        focusTab(tabs.length - 1);
        return;
      default:
    }
  }

  const selected = tabs.find((tab) => tab.id === value) ?? tabs[0];

  return (
    <div className="fo-tabs">
      <div className="fo-tabs__list" role="tablist" aria-label={label}>
        {tabs.map((tab, index) => {
          const isSelected = selected?.id === tab.id;
          return (
            <button
              key={tab.id}
              ref={(element) => {
                if (element === null) {
                  tabRefs.current.delete(tab.id);
                } else {
                  tabRefs.current.set(tab.id, element);
                }
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${tab.id}`}
              className="fo-tabs__tab"
              aria-selected={isSelected}
              aria-controls={`${base}-panel-${tab.id}`}
              // Roving tabindex: Tab enters the tablist once and leaves it once.
              tabIndex={isSelected ? 0 : -1}
              onClick={() => {
                onChange(tab.id);
              }}
              onKeyDown={(event) => {
                handleKeyDown(event, index);
              }}
            >
              <span>{tab.label}</span>
              {tab.badge === undefined ? null : <span className="fo-tabs__badge">{tab.badge}</span>}
            </button>
          );
        })}
      </div>

      {selected === undefined ? null : (
        <div
          className="fo-tabs__panel"
          role="tabpanel"
          id={`${base}-panel-${selected.id}`}
          aria-labelledby={`${base}-tab-${selected.id}`}
          // Focusable so a keyboard user can reach panel content that contains
          // no controls of its own.
          tabIndex={0}
        >
          {selected.panel}
        </div>
      )}
    </div>
  );
}
